import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import AgentDictionaryCollector from './AgentDictionaryCollector';
import * as XLSX from 'xlsx';
import { useApi } from '../../../../hooks/useApi';
import { saveSeoTransfers } from '../../../../components/OzonSeoUniqualization';
import logger from '../../../../utils/logger';
import './AutoSeoPanel.css';
import { filterBySearch } from '../../../../utils/search';

const PAGE_SIZE = 100;

function downloadSemanticsTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    ['Запрос', 'Частотность'],
    ['блесна на щуку зимняя', 12400],
    ['воблер для окуня', 8200],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Семантика');
  XLSX.writeFile(wb, 'autoseo_semantics_template.xlsx');
}

function exportSemantics(rows) {
  const ws = XLSX.utils.aoa_to_sheet([
    ['Запрос', 'Частотность'],
    ...rows.map(r => [r.query, r.frequency]),
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Семантика');
  XLSX.writeFile(wb, 'autoseo_semantics.xlsx');
}

function exportArticles(rows) {
  const ws = XLSX.utils.aoa_to_sheet([
    ['Артикул', 'Название', 'Свойства', 'SEO-описание'],
    ...rows.map(r => [r.offer_id, r.name || '', r.properties || '', r.generated_description || '']),
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Артикулы');
  XLSX.writeFile(wb, 'autoseo_articles.xlsx');
}

function parsePastedSemantics(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    const parts = line.split(/\t|;/).map(p => p.trim());
    if (!parts[0]) continue;
    if (parts[0].toLowerCase().includes('запрос')) continue;
    const frequency = parseInt(String(parts[1] || '0').replace(/\s/g, ''), 10) || 0;
    rows.push({ query: parts[0], frequency });
  }
  return rows;
}

function readXlsxRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array', cellStyles: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }));
      } catch {
        reject(new Error('Не удалось разобрать файл'));
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

async function readSemanticsXlsx(file) {
  const rows = await readXlsxRows(file);
  const out = [];
  for (const row of rows) {
    const query = String(row[0] || '').trim();
    if (!query || query.toLowerCase().includes('запрос')) continue;
    const frequency = parseInt(String(row[1] || '0').replace(/\s/g, ''), 10) || 0;
    out.push({ query, frequency });
  }
  return out;
}

async function readArticlesXlsx(file) {
  const rows = await readXlsxRows(file);
  const out = [];
  for (const row of rows) {
    const offerId = String(row[0] || '').trim();
    if (!offerId || offerId.toLowerCase() === 'артикул') continue;
    out.push({ offer_id: offerId, name: row[1] || null, properties: row[2] || null });
  }
  return out;
}

const PROPERTY_SOURCE_LABEL = {
  none: 'Пусто',
  all: 'Все атрибуты',
  description: 'Из описания',
};

export default function AutoSeoPanel({ accountId }) {
  const { authFetch, API_BASE_URL, request } = useApi();
  const BASE = `${API_BASE_URL}/api/v1/ozon-auto-seo`;

  const [activeTab, setActiveTab] = useState('semantics');

  // ── Словари (закладки Семантики) ──
  const [dictionaries, setDictionaries] = useState([]);
  const [dictionariesLoading, setDictionariesLoading] = useState(true);
  const [activeDictionaryId, setActiveDictionaryId] = useState(null);
  const [addingDictionary, setAddingDictionary] = useState(false);
  const [newDictionaryName, setNewDictionaryName] = useState('');
  const [renamingDictionaryId, setRenamingDictionaryId] = useState(null);
  const [renameDictionaryName, setRenameDictionaryName] = useState('');

  // ── Семантика ──
  const [semantics, setSemantics] = useState([]);
  const [semanticsLoading, setSemanticsLoading] = useState(true);
  const [semanticsMode, setSemanticsMode] = useState('file');
  const [pasteText, setPasteText] = useState('');
  const [semanticsSearch, setSemanticsSearch] = useState('');
  const [semanticsPage, setSemanticsPage] = useState(0);
  const [semanticsBusy, setSemanticsBusy] = useState(false);
  const [semanticsMessage, setSemanticsMessage] = useState('');
  const [semanticsSortDir, setSemanticsSortDir] = useState('desc'); // по частотности

  // ── Артикулы ──
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [propertiesSource, setPropertiesSource] = useState(null);
  const [articlesSearch, setArticlesSearch] = useState('');
  const [articlesPage, setArticlesPage] = useState(0);
  const [selectedOfferIds, setSelectedOfferIds] = useState(new Set());
  const [articlesBusy, setArticlesBusy] = useState(false);
  const [articlesMessage, setArticlesMessage] = useState('');
  const [bulkTemplateText, setBulkTemplateText] = useState('');
  const [showBulkTemplate, setShowBulkTemplate] = useState(false);
  // Per-article opt-out from the group's selected dictionaries (Шаг 4) — only
  // holds entries where the user removed at least one dictionary for that
  // offer_id; everything else falls back to the full genDictionaryIds set.
  const [articleDictOverrides, setArticleDictOverrides] = useState({});
  const [dictDropdownOfferId, setDictDropdownOfferId] = useState(null);
  const updateTimeoutsRef = useRef(new Map());
  const pendingUpdatesRef = useRef(new Map());
  const articlesFileRef = useRef();

  // ── Шаг 4: товары + словарь для генерации ──
  const [productFilters, setProductFilters] = useState(null); // { brands: [...], categories: [...] }
  const [productSource, setProductSource] = useState('all'); // all | offer_ids | brand | category | article_pattern
  const [genOfferIdsText, setGenOfferIdsText] = useState('');
  const [genSelectedBrands, setGenSelectedBrands] = useState([]);
  const [genSelectedCategoryKeys, setGenSelectedCategoryKeys] = useState([]);
  const [genArticlePattern, setGenArticlePattern] = useState('');
  const [genDictionaryIds, setGenDictionaryIds] = useState([]);
  const genOfferIdsFileRef = useRef();
  const categoryKey = c => `${c.category_id}:${c.type_id ?? '-'}`;

  // ── Словарь универсальных слов ──
  const [universalWordsText, setUniversalWordsText] = useState('');
  const [universalWordsCount, setUniversalWordsCount] = useState(0);
  const [universalWordsLoading, setUniversalWordsLoading] = useState(true);
  const [universalWordsBusy, setUniversalWordsBusy] = useState(false);
  const [universalWordsMessage, setUniversalWordsMessage] = useState('');
  const [universalWordsDirty, setUniversalWordsDirty] = useState(false);
  // Допуск близости характеристик, % (0 — только точные значения).
  const [measureTolerance, setMeasureTolerance] = useState(0);

  const loadDictionaries = useCallback(async () => {
    if (!accountId) return;
    setDictionariesLoading(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/dictionaries`);
      const list = data || [];
      setDictionaries(list);
      setActiveDictionaryId(prev => {
        if (prev && list.some(d => d.id === prev)) return prev;
        return list[0]?.id || null;
      });
      setGenDictionaryIds(prev => {
        const kept = prev.filter(id => list.some(d => d.id === id));
        return kept.length ? kept : (list[0] ? [list[0].id] : []);
      });
    } catch (error) {
      logger.error('Failed to load dictionaries:', error);
    } finally {
      setDictionariesLoading(false);
    }
  }, [accountId, authFetch, BASE]);

  const loadSemantics = useCallback(async () => {
    if (!accountId || !activeDictionaryId) { setSemantics([]); setSemanticsLoading(false); return; }
    setSemanticsLoading(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/dictionaries/${activeDictionaryId}/semantics`);
      setSemantics(data || []);
    } catch (error) {
      logger.error('Failed to load semantics:', error);
    } finally {
      setSemanticsLoading(false);
    }
  }, [accountId, activeDictionaryId, authFetch, BASE]);

  const loadArticles = useCallback(async () => {
    if (!accountId) return;
    setArticlesLoading(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/articles`);
      setArticles(data || []);
    } catch (error) {
      logger.error('Failed to load articles:', error);
    } finally {
      setArticlesLoading(false);
    }
  }, [accountId, authFetch, BASE]);

  const loadUniversalWords = useCallback(async () => {
    if (!accountId) return;
    setUniversalWordsLoading(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/universal-words`);
      setUniversalWordsText(data.words_text || '');
      setUniversalWordsCount(data.count || 0);
      setMeasureTolerance(Number(data.measure_tolerance_percent) || 0);
      setUniversalWordsDirty(false);
    } catch (error) {
      logger.error('Failed to load universal words:', error);
    } finally {
      setUniversalWordsLoading(false);
    }
  }, [accountId, authFetch, BASE]);

  useEffect(() => { loadDictionaries(); }, [loadDictionaries]);
  useEffect(() => { loadSemantics(); }, [loadSemantics]);
  useEffect(() => { loadArticles(); }, [loadArticles]);
  useEffect(() => { loadUniversalWords(); }, [loadUniversalWords]);
  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    authFetch(`${API_BASE_URL}/api/v1/ozon-ads/${accountId}/generator/product-filters`)
      .then(result => { if (!cancelled) setProductFilters(result); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [accountId, authFetch, API_BASE_URL]);
  useEffect(() => () => {
    updateTimeoutsRef.current.forEach(id => clearTimeout(id));
    updateTimeoutsRef.current.clear();
    pendingUpdatesRef.current.clear();
  }, []);
  useEffect(() => { setSemanticsPage(0); }, [semanticsSearch, activeDictionaryId, semanticsSortDir]);
  useEffect(() => { setArticlesPage(0); }, [articlesSearch, productSource, genOfferIdsText, genSelectedBrands, genSelectedCategoryKeys, genArticlePattern]);
  useEffect(() => {
    if (!dictDropdownOfferId) return;
    const close = () => setDictDropdownOfferId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [dictDropdownOfferId]);

  // ── Семантика: загрузка ──

  const applySemanticsRows = async rows => {
    if (!activeDictionaryId) { setSemanticsMessage('Сначала создайте словарь.'); return; }
    if (!rows.length) { setSemanticsMessage('Данные не распознаны. Формат: запрос [Tab] частотность.'); return; }
    setSemanticsBusy(true);
    try {
      await authFetch(`${BASE}/${accountId}/dictionaries/${activeDictionaryId}/semantics`, {
        method: 'PUT',
        body: JSON.stringify({ rows }),
      });
      await Promise.all([loadSemantics(), loadDictionaries()]);
      setSemanticsMessage(`Загружено ${rows.length} запросов`);
      setTimeout(() => setSemanticsMessage(''), 4000);
    } catch (error) {
      setSemanticsMessage('Ошибка загрузки: ' + error.message);
    } finally {
      setSemanticsBusy(false);
    }
  };

  const handleSemanticsFile = async e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (semantics.length && !window.confirm(
      `Заменить таблицу семантики словаря (${semantics.length} запросов) содержимым файла «${files[0].name}»?`
    )) return;
    setSemanticsBusy(true);
    try {
      const rows = await readSemanticsXlsx(files[0]);
      await applySemanticsRows(rows);
    } catch (error) {
      setSemanticsMessage('Ошибка чтения файла: ' + error.message);
      setSemanticsBusy(false);
    }
  };

  const handleSemanticsPaste = async () => {
    if (!pasteText.trim()) return;
    const rows = parsePastedSemantics(pasteText);
    await applySemanticsRows(rows);
    setPasteText('');
  };

  const clearSemantics = async () => {
    if (!activeDictionaryId) return;
    if (!window.confirm('Удалить всю таблицу «Семантика» этого словаря? Это действие необратимо.')) return;
    setSemanticsBusy(true);
    try {
      await authFetch(`${BASE}/${accountId}/dictionaries/${activeDictionaryId}/semantics`, { method: 'DELETE' });
      await Promise.all([loadSemantics(), loadDictionaries()]);
    } catch (error) {
      setSemanticsMessage('Ошибка очистки: ' + error.message);
    } finally {
      setSemanticsBusy(false);
    }
  };

  const activeDictionary = dictionaries.find(d => d.id === activeDictionaryId) || null;
  // Словарь, розданный на проект другим кабинетом: только просмотр и генерация.
  const activeDictionaryReadOnly = activeDictionary ? activeDictionary.is_own === false : false;

  const sortedSemantics = useMemo(() => {
    const sign = semanticsSortDir === 'asc' ? 1 : -1;
    return [...semantics].sort((a, b) => sign * ((a.frequency || 0) - (b.frequency || 0)));
  }, [semantics, semanticsSortDir]);
  const filteredSemantics = filterBySearch(sortedSemantics, semanticsSearch);
  const semanticsPageCount = Math.max(1, Math.ceil(filteredSemantics.length / PAGE_SIZE));
  const visibleSemantics = filteredSemantics.slice(semanticsPage * PAGE_SIZE, (semanticsPage + 1) * PAGE_SIZE);

  // ── Словари: create / rename / delete ──

  const createDictionary = async () => {
    const name = newDictionaryName.trim();
    if (!name) return;
    setDictionariesLoading(true);
    try {
      const dictionary = await authFetch(`${BASE}/${accountId}/dictionaries`, {
        method: 'POST', body: JSON.stringify({ name }),
      });
      setNewDictionaryName('');
      setAddingDictionary(false);
      await loadDictionaries();
      setActiveDictionaryId(dictionary.id);
      setGenDictionaryIds([dictionary.id]);
    } catch (error) {
      setSemanticsMessage('Ошибка создания словаря: ' + error.message);
      setDictionariesLoading(false);
    }
  };

  const startRenameDictionary = dictionary => {
    setRenamingDictionaryId(dictionary.id);
    setRenameDictionaryName(dictionary.name);
  };

  const confirmRenameDictionary = async () => {
    const name = renameDictionaryName.trim();
    if (!name || !renamingDictionaryId) { setRenamingDictionaryId(null); return; }
    try {
      await authFetch(`${BASE}/${accountId}/dictionaries/${renamingDictionaryId}`, {
        method: 'PATCH', body: JSON.stringify({ name }),
      });
      setRenamingDictionaryId(null);
      await loadDictionaries();
    } catch (error) {
      setSemanticsMessage('Ошибка переименования: ' + error.message);
    }
  };

  const toggleDictionaryShare = async dictionary => {
    const shared = !dictionary.shared_with_project;
    const question = shared
      ? `Раздать словарь «${dictionary.name}» на проект? Им смогут пользоваться все кабинеты проекта (только чтение и генерация).`
      : `Закрыть доступ к словарю «${dictionary.name}» для других кабинетов проекта?`;
    if (!window.confirm(question)) return;
    try {
      await authFetch(`${BASE}/${accountId}/dictionaries/${dictionary.id}/share`, {
        method: 'PATCH', body: JSON.stringify({ shared_with_project: shared }),
      });
      await loadDictionaries();
      setSemanticsMessage(shared ? 'Словарь роздан на проект' : 'Словарь больше не раздаётся на проект');
      setTimeout(() => setSemanticsMessage(''), 4000);
    } catch (error) {
      setSemanticsMessage('Ошибка: ' + error.message);
    }
  };

  const deleteDictionary = async dictionary => {
    if (!window.confirm(`Удалить словарь «${dictionary.name}» и все его запросы? Это действие необратимо.`)) return;
    try {
      await authFetch(`${BASE}/${accountId}/dictionaries/${dictionary.id}`, { method: 'DELETE' });
      await loadDictionaries();
    } catch (error) {
      setSemanticsMessage('Ошибка удаления словаря: ' + error.message);
    }
  };

  // ── Шаг 1: импорт ассортимента ──

  const importFromAssortment = async () => {
    setArticlesBusy(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/articles/import-from-assortment`, { method: 'POST' });
      await loadArticles();
      setArticlesMessage(`Загружено/обновлено артикулов: ${data.imported}`);
      setTimeout(() => setArticlesMessage(''), 4000);
    } catch (error) {
      setArticlesMessage('Ошибка загрузки: ' + error.message);
    } finally {
      setArticlesBusy(false);
    }
  };

  // ── Шаг 2: источник свойств ──

  const applyPropertiesSource = async source => {
    if (source !== 'none' && articles.length === 0) {
      setArticlesMessage('Сначала выполните Шаг 1 — загрузите ассортимент');
      return;
    }
    setArticlesBusy(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/articles/apply-properties`, {
        method: 'POST', body: JSON.stringify({ source }),
      });
      await loadArticles();
      setPropertiesSource(source);
      setArticlesMessage(`Свойства (${PROPERTY_SOURCE_LABEL[source]}) применены к ${data.updated} артикулам`);
      setTimeout(() => setArticlesMessage(''), 4000);
    } catch (error) {
      setArticlesMessage('Ошибка: ' + error.message);
    } finally {
      setArticlesBusy(false);
    }
  };

  // ── Шаг 3: словарь универсальных слов ──

  const saveUniversalWords = async () => {
    setUniversalWordsBusy(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/universal-words`, {
        method: 'PUT',
        body: JSON.stringify({
          words_text: universalWordsText,
          measure_tolerance_percent: Number(measureTolerance) || 0,
        }),
      });
      setUniversalWordsCount(data.count || 0);
      setUniversalWordsDirty(false);
      setUniversalWordsMessage(`Сохранено слов: ${data.count}`);
      setTimeout(() => setUniversalWordsMessage(''), 4000);
    } catch (error) {
      setUniversalWordsMessage('Ошибка сохранения: ' + error.message);
    } finally {
      setUniversalWordsBusy(false);
    }
  };

  // Inline edit (debounced), consistent with the existing SEO Уникализация keyword editor.
  const updateArticle = (articleId, updates) => {
    setArticles(prev => prev.map(a => (a.id === articleId ? { ...a, ...updates } : a)));
    const pending = { ...(pendingUpdatesRef.current.get(articleId) || {}), ...updates };
    pendingUpdatesRef.current.set(articleId, pending);

    const existing = updateTimeoutsRef.current.get(articleId);
    if (existing) clearTimeout(existing);

    const timeoutId = setTimeout(async () => {
      const payload = pendingUpdatesRef.current.get(articleId);
      updateTimeoutsRef.current.delete(articleId);
      pendingUpdatesRef.current.delete(articleId);
      if (!payload) return;
      try {
        await authFetch(`${BASE}/${accountId}/articles/${articleId}`, {
          method: 'PATCH', body: JSON.stringify(payload),
        });
      } catch (error) {
        logger.error('Failed to update article:', error);
        await loadArticles();
      }
    }, 600);
    updateTimeoutsRef.current.set(articleId, timeoutId);
  };

  const applyBulkRows = async rows => {
    if (!rows.length) { setArticlesMessage('Строки не распознаны. Формат: артикул [Tab] название [Tab] свойства.'); return; }
    setArticlesBusy(true);
    try {
      const data = await authFetch(`${BASE}/${accountId}/articles/bulk-update`, {
        method: 'POST', body: JSON.stringify({ rows }),
      });
      await loadArticles();
      setArticlesMessage(`Обновлено строк: ${data.updated}`);
      setBulkTemplateText(''); setShowBulkTemplate(false);
    } catch (error) {
      setArticlesMessage('Ошибка: ' + error.message);
    } finally {
      setArticlesBusy(false);
    }
  };

  const handleBulkTemplate = () => {
    if (!bulkTemplateText.trim()) return;
    const lines = bulkTemplateText.trim().split('\n').map(l => l.trim()).filter(Boolean);
    const rows = [];
    for (const line of lines) {
      const parts = line.split('\t');
      if (!parts[0] || parts[0].toLowerCase() === 'артикул') continue;
      rows.push({ offer_id: parts[0].trim(), name: parts[1] ?? null, properties: parts[2] ?? null });
    }
    applyBulkRows(rows);
  };

  const clearArticlesTable = async () => {
    if (!window.confirm('Удалить все загруженные артикулы (включая свойства и сгенерированные описания)? Это действие необратимо — используйте, если хотите загрузить таблицу шаблоном заново.')) return;
    setArticlesBusy(true);
    try {
      await authFetch(`${BASE}/${accountId}/articles`, { method: 'DELETE' });
      setSelectedOfferIds(new Set());
      await loadArticles();
      setArticlesMessage('Таблица артикулов очищена');
      setTimeout(() => setArticlesMessage(''), 4000);
    } catch (error) {
      setArticlesMessage('Ошибка очистки: ' + error.message);
    } finally {
      setArticlesBusy(false);
    }
  };

  const handleArticlesFile = async e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    try {
      const rows = await readArticlesXlsx(files[0]);
      await applyBulkRows(rows);
    } catch (error) {
      setArticlesMessage('Ошибка чтения файла: ' + error.message);
    }
  };

  // ── Шаг 3: генерация ──

  const toggleSelectOffer = offerId => {
    setSelectedOfferIds(prev => {
      const next = new Set(prev);
      if (next.has(offerId)) next.delete(offerId); else next.add(offerId);
      return next;
    });
  };

  const toggleSelectAllVisible = visible => {
    setSelectedOfferIds(prev => {
      const next = new Set(prev);
      const allSelected = visible.every(a => next.has(a.offer_id));
      visible.forEach(a => { if (allSelected) next.delete(a.offer_id); else next.add(a.offer_id); });
      return next;
    });
  };

  function parseGenOfferIds(text) {
    return Array.from(new Set(text.split(/[\r\n,;\t]+/).map(s => s.trim()).filter(Boolean)));
  }

  function handleGenOfferIdsFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const isSpreadsheet = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      let text = '';
      if (isSpreadsheet) {
        try {
          const wb = XLSX.read(reader.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
          text = rows.map(row => (row && row.length ? row[0] : ''))
            .filter(v => v !== undefined && v !== null && String(v).trim())
            .join('\n');
        } catch {
          setArticlesMessage('Не удалось прочитать файл. Убедитесь, что это корректный XLSX/CSV.');
          return;
        }
      } else {
        text = String(reader.result || '');
      }
      setGenOfferIdsText(prev => (prev.trim() ? prev.trim() + '\n' + text : text));
    };
    if (isSpreadsheet) reader.readAsArrayBuffer(file); else reader.readAsText(file);
  }

  // Dictionaries that will actually be used for a given article: its override
  // (once touched, a fully independent list — can include dictionaries outside
  // the Шаг 4 group selection too), else the group selection as-is.
  const effectiveArticleDictIds = offerId => articleDictOverrides[offerId] || genDictionaryIds;

  const toggleArticleDictionary = (offerId, dictId) => {
    setArticleDictOverrides(prev => {
      const current = prev[offerId] || genDictionaryIds;
      const next = current.includes(dictId) ? current.filter(id => id !== dictId) : [...current, dictId];
      return { ...prev, [offerId]: next };
    });
  };

  const resetArticleDictionaries = offerId => {
    setArticleDictOverrides(prev => {
      if (!(offerId in prev)) return prev;
      const next = { ...prev };
      delete next[offerId];
      return next;
    });
  };

  const generateSeo = async () => {
    if (genDictionaryIds.length === 0) { setArticlesMessage('Выберите хотя бы один словарь для генерации.'); return; }
    // Only forward overrides that actually differ from the group's full
    // selection — keeps the payload small and lets the backend fall back to
    // `dictionary_ids` for every untouched article.
    const overridesPayload = {};
    for (const [offerId, ids] of Object.entries(articleDictOverrides)) {
      const isSameAsGroup = ids.length === genDictionaryIds.length
        && genDictionaryIds.every(id => ids.includes(id));
      if (!isSameAsGroup) overridesPayload[offerId] = ids;
    }
    const body = {
      dictionary_ids: genDictionaryIds,
      ...(Object.keys(overridesPayload).length > 0 ? { article_dictionary_ids: overridesPayload } : {}),
      // "selected" is a frontend-only source — it reuses the backend's offer_ids
      // filter with the rows checked in the table below.
      product_source: productSource === 'selected' ? 'offer_ids' : productSource,
    };
    if (productSource === 'selected') {
      if (selectedOfferIds.size === 0) { setArticlesMessage('Отметьте хотя бы один артикул в таблице.'); return; }
      body.offer_ids = Array.from(selectedOfferIds);
    } else if (productSource === 'offer_ids') {
      const offer_ids = parseGenOfferIds(genOfferIdsText);
      if (offer_ids.length === 0) { setArticlesMessage('Вставьте хотя бы один артикул.'); return; }
      body.offer_ids = offer_ids;
    } else if (productSource === 'brand') {
      if (genSelectedBrands.length === 0) { setArticlesMessage('Выберите хотя бы один бренд.'); return; }
      body.brands = genSelectedBrands;
    } else if (productSource === 'category') {
      if (genSelectedCategoryKeys.length === 0) { setArticlesMessage('Выберите хотя бы одну категорию.'); return; }
      body.category_ids = Array.from(new Set(genSelectedCategoryKeys.map(k => Number(k.split(':')[0]))));
      const typeIds = genSelectedCategoryKeys.map(k => k.split(':')[1]).filter(v => v !== '-').map(Number);
      if (typeIds.length) body.type_ids = typeIds;
    } else if (productSource === 'article_pattern') {
      if (!genArticlePattern.trim()) { setArticlesMessage('Введите часть артикула.'); return; }
      body.article_pattern = genArticlePattern.trim();
    }

    setArticlesBusy(true);
    setArticlesMessage('Генерация SEO-описаний…');
    try {
      const results = await authFetch(`${BASE}/${accountId}/generate`, {
        method: 'POST', body: JSON.stringify(body),
      });
      await loadArticles();
      setArticleDictOverrides({});
      setArticlesMessage(`Сгенерировано описаний: ${results.length}`);
      setTimeout(() => setArticlesMessage(''), 5000);
    } catch (error) {
      setArticlesMessage('Ошибка генерации: ' + error.message);
    } finally {
      setArticlesBusy(false);
    }
  };

  const transferToSeo = async () => {
    const targets = articles.filter(a => selectedOfferIds.has(a.offer_id) && a.generated_description);
    if (targets.length === 0) { setArticlesMessage('Выберите артикулы с готовым SEO-описанием'); return; }
    const transfers = {};
    for (const a of targets) {
      transfers[a.offer_id] = { originalDesc: '', seoText: a.generated_description, name: a.name || '' };
    }
    if (!(await saveSeoTransfers(request, accountId, transfers))) {
      setArticlesMessage('Не удалось сохранить перенос на сервере. Попробуйте ещё раз.');
      return;
    }
    setArticlesMessage(`Перенесено в ${targets.length} товар(ов). Перейдите в SEO → Описания.`);
    setTimeout(() => setArticlesMessage(''), 8000);
  };

  // Выбор товаров на Шаге 4 сразу фильтрует таблицу — те же правила, что у генерации на бэкенде.
  // «Отмеченные в таблице» не фильтрует: иначе нельзя было бы отметить новые строки.
  const scopedArticles = useMemo(() => {
    if (productSource === 'offer_ids') {
      const ids = new Set(parseGenOfferIds(genOfferIdsText));
      return ids.size ? articles.filter(a => ids.has(a.offer_id)) : articles;
    }
    if (productSource === 'brand') {
      if (!genSelectedBrands.length) return articles;
      const brands = new Set(genSelectedBrands);
      return articles.filter(a => brands.has(a.brand));
    }
    if (productSource === 'category') {
      if (!genSelectedCategoryKeys.length) return articles;
      const categoryIds = new Set(genSelectedCategoryKeys.map(k => Number(k.split(':')[0])));
      const typeIds = new Set(genSelectedCategoryKeys.map(k => k.split(':')[1]).filter(v => v !== '-').map(Number));
      return articles.filter(a => categoryIds.has(a.category_id) && (!typeIds.size || typeIds.has(a.type_id)));
    }
    if (productSource === 'article_pattern') {
      const pattern = genArticlePattern.trim().toLowerCase();
      return pattern ? articles.filter(a => a.offer_id.toLowerCase().includes(pattern)) : articles;
    }
    return articles;
  }, [articles, productSource, genOfferIdsText, genSelectedBrands, genSelectedCategoryKeys, genArticlePattern]);
  const isArticlesScoped = scopedArticles !== articles;

  const filteredArticles = filterBySearch(scopedArticles, articlesSearch);
  const articlesPageCount = Math.max(1, Math.ceil(filteredArticles.length / PAGE_SIZE));
  const visibleArticles = filteredArticles.slice(articlesPage * PAGE_SIZE, (articlesPage + 1) * PAGE_SIZE);

  const allVisibleSelected = useMemo(
    () => visibleArticles.length > 0 && visibleArticles.every(a => selectedOfferIds.has(a.offer_id)),
    [visibleArticles, selectedOfferIds]
  );

  return (
    <div className="autoseo-panel">
      <div className="autoseo-tabs">
        <button type="button" className={`autoseo-tab ${activeTab === 'semantics' ? 'autoseo-tab--active' : ''}`}
          onClick={() => setActiveTab('semantics')}>
          Семантика <span className="autoseo-count">{semantics.length.toLocaleString('ru-RU')}</span>
        </button>
        <button type="button" className={`autoseo-tab ${activeTab === 'articles' ? 'autoseo-tab--active' : ''}`}
          onClick={() => setActiveTab('articles')}>
          Артикулы <span className="autoseo-count">{articles.length.toLocaleString('ru-RU')}</span>
        </button>
        <button
          type="button"
          className="btn-secondary btn-sm"
          style={{ marginLeft: 'auto' }}
          disabled={activeTab === 'semantics' ? (dictionariesLoading || semanticsLoading) : articlesLoading}
          onClick={() => {
            if (activeTab === 'semantics') { loadDictionaries(); loadSemantics(); loadUniversalWords(); }
            else { loadArticles(); }
          }}
        >
          ↺ Обновить
        </button>
      </div>

      {/* ───────────── Семантика ───────────── */}
      {activeTab === 'semantics' && (
        <>
          <div className="autoseo-instructions">
            Несколько именованных словарей семантики — по закладке на каждую товарную линейку/сезон.
            Таблица ключевых запросов и их частотности (2 столбца) загружается отдельно для каждого словаря.
            Выбранный на Шаге 4 (вкладка «Артикулы») словарь используется как источник для генерации SEO.
          </div>

          <div className="autoseo-dict-tabs">
            {dictionariesLoading && dictionaries.length === 0 && (
              <span className="autoseo-hint">Загрузка словарей…</span>
            )}
            {dictionaries.map(d => (
              renamingDictionaryId === d.id ? (
                <div key={d.id} className="autoseo-dict-tab autoseo-dict-tab--editing">
                  <input
                    type="text" autoFocus value={renameDictionaryName}
                    onChange={e => setRenameDictionaryName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') confirmRenameDictionary(); if (e.key === 'Escape') setRenamingDictionaryId(null); }}
                    onBlur={confirmRenameDictionary}
                    className="autoseo-textinput"
                  />
                </div>
              ) : (
                <div key={d.id} className={`autoseo-dict-tab ${activeDictionaryId === d.id ? 'autoseo-dict-tab--active' : ''} ${d.is_own === false ? 'autoseo-dict-tab--foreign' : ''}`}>
                  <button type="button" className="autoseo-dict-tab__btn" onClick={() => setActiveDictionaryId(d.id)}
                    onDoubleClick={() => { if (d.is_own !== false) startRenameDictionary(d); }}
                    title={d.is_own === false ? `Роздан на проект кабинетом «${d.owner_account_name || '—'}» — только просмотр` : undefined}>
                    {d.name} <span className="autoseo-count">{(d.keyword_count || 0).toLocaleString('ru-RU')}</span>
                    {d.is_own === false && <span className="autoseo-dict-tab__owner">{d.owner_account_name}</span>}
                  </button>
                  {d.is_own !== false && <>
                    <button type="button"
                      className={`autoseo-dict-tab__icon ${d.shared_with_project ? 'autoseo-dict-tab__icon--on' : ''}`}
                      title={d.shared_with_project ? 'Роздан на проект — нажмите, чтобы закрыть доступ' : 'Раздать на проект'}
                      onClick={() => toggleDictionaryShare(d)}>
                      <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13Zm8 0c-.29 0-.62.02-.97.05A4.22 4.22 0 0 1 17 16.5V19h6v-2.5c0-2.33-4.67-3.5-7-3.5Z" /></svg>
                    </button>
                    <button type="button" className="autoseo-dict-tab__icon" title="Переименовать"
                      onClick={() => startRenameDictionary(d)}>
                      <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.21a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83Z" /></svg>
                    </button>
                    <button type="button" className="autoseo-dict-tab__icon autoseo-dict-tab__icon--danger" title="Удалить словарь"
                      onClick={() => deleteDictionary(d)}>
                      <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4Z" /></svg>
                    </button>
                  </>}
                </div>
              )
            ))}
            {addingDictionary ? (
              <div className="autoseo-dict-tab autoseo-dict-tab--editing">
                <input
                  type="text" autoFocus placeholder="Название словаря" value={newDictionaryName}
                  onChange={e => setNewDictionaryName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') createDictionary(); if (e.key === 'Escape') { setAddingDictionary(false); setNewDictionaryName(''); } }}
                  className="autoseo-textinput"
                />
                <button type="button" className="autoseo-btn autoseo-btn--primary" onClick={createDictionary}>OK</button>
                <button type="button" className="autoseo-btn" onClick={() => { setAddingDictionary(false); setNewDictionaryName(''); }}>Отмена</button>
              </div>
            ) : (
              <button type="button" className="autoseo-btn autoseo-dict-tab__add" onClick={() => setAddingDictionary(true)}>
                + Новый словарь
              </button>
            )}
          </div>

          <AgentDictionaryCollector authFetch={authFetch} base={BASE} accountId={accountId}
            onDictionary={(dictionary) => { loadDictionaries().then(() => { setActiveDictionaryId(dictionary.id); }); }} />

          {activeDictionaryReadOnly && (
            <div className="autoseo-hint" style={{ padding: '0 16px 12px' }}>
              Этот словарь роздан на проект кабинетом «{activeDictionary.owner_account_name || '—'}».
              Его можно просматривать и использовать для генерации; менять запросы может только кабинет-владелец.
            </div>
          )}

          {!activeDictionaryId && !dictionariesLoading && (
            <div className="autoseo-hint" style={{ padding: '0 16px 12px' }}>
              Словарей ещё нет — создайте первый кнопкой «+ Новый словарь».
            </div>
          )}

          <div className="autoseo-toolbar">
            <button type="button" className={`autoseo-btn ${semanticsMode === 'file' ? 'autoseo-btn--primary' : ''}`}
              onClick={() => setSemanticsMode('file')}>Загрузить файл</button>
            <button type="button" className={`autoseo-btn ${semanticsMode === 'paste' ? 'autoseo-btn--primary' : ''}`}
              onClick={() => setSemanticsMode('paste')}>Вставить (Ctrl+V)</button>
            <button type="button" className="autoseo-btn" onClick={downloadSemanticsTemplate}>↓ Шаблон</button>
            <button type="button" className="autoseo-btn" onClick={() => exportSemantics(semantics)}
              disabled={semantics.length === 0}>↓ Экспорт таблицы</button>
            <button type="button" className="autoseo-btn autoseo-btn--danger" onClick={clearSemantics}
              disabled={semanticsBusy || semantics.length === 0 || !activeDictionaryId || activeDictionaryReadOnly}>Очистить всё</button>
            <div className="autoseo-spacer" />
            <input type="text" placeholder="Поиск по запросам..." value={semanticsSearch}
              onChange={e => setSemanticsSearch(e.target.value)}
              className="autoseo-search" />
          </div>

          {semanticsMode === 'file' && (
            <div className="autoseo-upload">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleSemanticsFile} disabled={semanticsBusy || !activeDictionaryId || activeDictionaryReadOnly} />
              <div className="autoseo-hint">
                2 столбца: Запрос, Частотность. Загрузка полностью заменяет текущую таблицу. Таблица может содержать 20 000–100 000 строк.
              </div>
            </div>
          )}
          {semanticsMode === 'paste' && (
            <div className="autoseo-upload">
              <textarea
                rows={8}
                placeholder={'Запрос\tЧастотность\nблесна на щуку зимняя\t12400\nвоблер для окуня\t8200'}
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
              />
              <div style={{ marginTop: 8 }}>
                <button type="button" className="autoseo-btn autoseo-btn--primary"
                  onClick={handleSemanticsPaste} disabled={semanticsBusy || !activeDictionaryId || activeDictionaryReadOnly}>Загрузить (заменить таблицу)</button>
              </div>
            </div>
          )}

          {semanticsMessage && <div className="autoseo-message">{semanticsMessage}</div>}

          {semanticsLoading ? <div className="autoseo-hint" style={{ padding: 16 }}>Загрузка…</div> : (
            <>
              <div className="autoseo-scroll">
                <table className="autoseo-table">
                  <thead>
                    <tr>
                      <th className="autoseo-th autoseo-th--num">№</th>
                      <th className="autoseo-th autoseo-th--query">Запрос</th>
                      <th className="autoseo-th autoseo-th--freq autoseo-th--sortable"
                        title="Сортировать по частотности"
                        onClick={() => setSemanticsSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}>
                        Частотность {semanticsSortDir === 'desc' ? '↓' : '↑'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSemantics.map((row, idx) => (
                      <tr className="autoseo-tr" key={row.id}>
                        <td className="autoseo-td autoseo-td--num">{semanticsPage * PAGE_SIZE + idx + 1}</td>
                        <td className="autoseo-td autoseo-td--query">{row.query}</td>
                        <td className="autoseo-td autoseo-td--freq">{row.frequency.toLocaleString('ru-RU')}</td>
                      </tr>
                    ))}
                    {visibleSemantics.length === 0 && (
                      <tr><td className="autoseo-td autoseo-td--empty" colSpan={3}>
                        Пусто. Загрузите таблицу семантики файлом или вставкой.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {semanticsPageCount > 1 && (
                <div className="autoseo-pagination">
                  <button className="autoseo-btn" disabled={semanticsPage === 0}
                    onClick={() => setSemanticsPage(p => Math.max(0, p - 1))}>←</button>
                  <span className="autoseo-pagination__info">{semanticsPage + 1} / {semanticsPageCount}</span>
                  <button className="autoseo-btn" disabled={semanticsPage >= semanticsPageCount - 1}
                    onClick={() => setSemanticsPage(p => Math.min(semanticsPageCount - 1, p + 1))}>→</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ───────────── Артикулы ───────────── */}
      {activeTab === 'articles' && (
        <>
          {/* Шаг 1 */}
          <div className="autoseo-step">
            <div className="autoseo-step-head">
              <span className="autoseo-step-num">1</span>
              <span className="autoseo-step-title">Загрузить ассортимент из OZON</span>
            </div>
            <div className="autoseo-instructions">
              Подтягивает Артикул и Название из раздела «Ассортимент — Артикулы». Свойства этим шагом не трогаются —
              их источник выбирается отдельно на Шаге 2. Повторный запуск обновляет названия и добавляет новые артикулы,
              не удаляя уже загруженные.
            </div>
            <div className="autoseo-toolbar">
              <button type="button" className="autoseo-btn autoseo-btn--primary" onClick={importFromAssortment} disabled={articlesBusy}>
                Загрузить из Ассортимент — Артикулы
              </button>
            </div>
          </div>

          {/* Шаг 2 */}
          <div className="autoseo-step">
            <div className="autoseo-step-head">
              <span className="autoseo-step-num">2</span>
              <span className="autoseo-step-title">Свойства</span>
            </div>
            <div className="autoseo-instructions">
              Заполняет столбец «Свойства» уникальными словами для КАЖДОГО загруженного артикула. Выберите один источник —
              он применяется сразу ко всем артикулам и полностью заменяет предыдущее содержимое столбца.
              Свойства разделяются «;». Между «;» можно вписать словосочетание, например «крупный окунь»: тогда
              подойдут только запросы, где есть все его слова — в любом порядке и форме («воблер на окуня крупного»),
              а «воблер на окуня» — нет.
            </div>
            <div className="autoseo-toolbar">
              <button type="button" className={`autoseo-btn ${propertiesSource === 'none' ? 'autoseo-btn--primary' : ''}`}
                onClick={() => applyPropertiesSource('none')} disabled={articlesBusy || articles.length === 0}>
                2.1 Оставить пустым
              </button>
              <button type="button" className={`autoseo-btn ${propertiesSource === 'all' ? 'autoseo-btn--primary' : ''}`}
                onClick={() => applyPropertiesSource('all')} disabled={articlesBusy || articles.length === 0}>
                2.2 Из всех атрибутов
              </button>
              <button type="button" className={`autoseo-btn ${propertiesSource === 'description' ? 'autoseo-btn--primary' : ''}`}
                onClick={() => applyPropertiesSource('description')} disabled={articlesBusy || articles.length === 0}>
                2.3 Из описания
              </button>
            </div>
          </div>

          {/* Шаг 3 */}
          <div className="autoseo-step">
            <div className="autoseo-step-head">
              <span className="autoseo-step-num">3</span>
              <span className="autoseo-step-title">Словарь универсальных слов</span>
            </div>
            <div className="autoseo-instructions">
              Слова или основы слов, которые не обязаны буквально встречаться в Названии/Свойствах артикула,
              чтобы запрос из «Семантики» всё равно считался подходящим. Пример: если добавить основу «рыболовн»,
              запрос «рыболовная блесна на щуку» подойдёт товару, даже если слова «рыболовная» нет в его названии
              или свойствах. По одному слову на строку или через «;». Необязательный шаг — можно оставить пустым.
            </div>
            <div className="autoseo-upload">
              <textarea
                rows={4}
                placeholder={'рыболовн\nспиннинговый\nуниверсальный'}
                value={universalWordsText}
                onChange={e => { setUniversalWordsText(e.target.value); setUniversalWordsDirty(true); }}
              />
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <label htmlFor="autoseo-tolerance">Допуск близости характеристик, %</label>
                <input
                  id="autoseo-tolerance"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  style={{ width: 80 }}
                  value={measureTolerance}
                  onChange={e => {
                    setMeasureTolerance(e.target.value === '' ? 0 : Number(e.target.value));
                    setUniversalWordsDirty(true);
                  }}
                />
                <span className="autoseo-hint" style={{ marginTop: 0 }}>
                  0 — подходят только точные значения товара (и попадание в его диапазон).
                  25 — ещё и близкие: товару 100мм подойдёт запрос «воблер 9см», но не «воблер 5см».
                  Вес сверяется только с весом, размер — только с размером.
                </span>
              </div>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button type="button" className="autoseo-btn autoseo-btn--primary"
                  onClick={saveUniversalWords} disabled={universalWordsBusy || universalWordsLoading}>
                  Сохранить словарь
                </button>
                {!universalWordsLoading && !universalWordsDirty && (
                  <span className="autoseo-hint" style={{ marginTop: 0 }}>Сохранено слов: {universalWordsCount}</span>
                )}
              </div>
            </div>
            {universalWordsMessage && <div className="autoseo-message">{universalWordsMessage}</div>}
          </div>

          {/* Шаг 4 */}
          <div className="autoseo-step">
            <div className="autoseo-step-head">
              <span className="autoseo-step-num">4</span>
              <span className="autoseo-step-title">Товары, словарь и генерация SEO</span>
            </div>
            <div className="autoseo-instructions">
              Выберите товары для генерации и словарь семантики — источник запросов. Подбирает подходящие
              запросы под Название + Свойства каждого выбранного артикула и собирает готовое SEO-описание
              (до 6000 символов), помечая каждый артикул выбранным словарём. Затем «Перенести в SEO» отправляет
              выбранные описания в SEO → Описания на проверку перед публикацией на OZON.
            </div>

            <div className="autoseo-gen-section">
              <div className="autoseo-gen-section-title">Товары для генерации</div>
              <div className="autoseo-gen-options">
                {[
                  { value: 'all', label: 'Весь ассортимент' },
                  { value: 'offer_ids', label: 'По списку артикулов' },
                  { value: 'selected', label: `Отмеченные в таблице (${selectedOfferIds.size})` },
                  { value: 'brand', label: 'По бренду' },
                  { value: 'category', label: 'По категории' },
                  { value: 'article_pattern', label: 'По части артикула' },
                ].map(o => (
                  <label key={o.value} className={`autoseo-gen-option ${productSource === o.value ? 'selected' : ''}`}>
                    <input type="radio" name="autoseo_product_source" value={o.value}
                      checked={productSource === o.value} onChange={() => setProductSource(o.value)} />
                    {o.label}
                  </label>
                ))}
              </div>

              {productSource === 'offer_ids' && (
                <div className="autoseo-gen-offer-ids-block">
                  <textarea
                    className="autoseo-gen-offer-ids-textarea"
                    placeholder="Вставьте offer_id — каждый с новой строки или через запятую"
                    value={genOfferIdsText}
                    onChange={e => setGenOfferIdsText(e.target.value)}
                    rows={4}
                  />
                  <div className="autoseo-gen-offer-ids-row">
                    <label className="autoseo-btn autoseo-gen-offer-ids-upload">
                      Загрузить файл (.txt/.csv/.xlsx)
                      <input ref={genOfferIdsFileRef} type="file" accept=".txt,.csv,.xlsx,.xls"
                        onChange={handleGenOfferIdsFile} style={{ display: 'none' }} />
                    </label>
                    <span className="autoseo-hint">
                      {parseGenOfferIds(genOfferIdsText).length > 0 ? `${parseGenOfferIds(genOfferIdsText).length} артикулов` : 'ничего не введено'}
                    </span>
                  </div>
                </div>
              )}

              {productSource === 'brand' && (
                <div className="autoseo-gen-checklist">
                  {!productFilters && <span className="autoseo-hint">Загрузка списка брендов…</span>}
                  {productFilters && !productFilters.brands.length && <span className="autoseo-hint">Бренды не найдены</span>}
                  {productFilters?.brands.map(b => (
                    <label key={b.brand} className={`autoseo-gen-option ${genSelectedBrands.includes(b.brand) ? 'selected' : ''}`}>
                      <input type="checkbox" checked={genSelectedBrands.includes(b.brand)}
                        onChange={() => setGenSelectedBrands(s => s.includes(b.brand) ? s.filter(x => x !== b.brand) : [...s, b.brand])} />
                      {b.brand} <span className="autoseo-hint">({b.count})</span>
                    </label>
                  ))}
                </div>
              )}

              {productSource === 'category' && (
                <div className="autoseo-gen-checklist">
                  {!productFilters && <span className="autoseo-hint">Загрузка списка категорий…</span>}
                  {productFilters && !productFilters.categories.length && <span className="autoseo-hint">Категории не найдены</span>}
                  {productFilters?.categories.map(c => {
                    const key = categoryKey(c);
                    return (
                      <label key={key} className={`autoseo-gen-option ${genSelectedCategoryKeys.includes(key) ? 'selected' : ''}`}>
                        <input type="checkbox" checked={genSelectedCategoryKeys.includes(key)}
                          onChange={() => setGenSelectedCategoryKeys(s => s.includes(key) ? s.filter(x => x !== key) : [...s, key])} />
                        {c.name || `Категория ${c.category_id}`} <span className="autoseo-hint">({c.count})</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {productSource === 'article_pattern' && (
                <div className="autoseo-gen-offer-ids-block">
                  <input
                    type="text" className="autoseo-textinput"
                    placeholder="Часть артикула, например AB-2024"
                    value={genArticlePattern}
                    onChange={e => setGenArticlePattern(e.target.value)}
                  />
                  <div className="autoseo-hint">
                    {genArticlePattern.trim()
                      ? `Совпадает артикулов: ${articles.filter(a => a.offer_id.toLowerCase().includes(genArticlePattern.trim().toLowerCase())).length} из ${articles.length}`
                      : 'Найдём все загруженные артикулы, содержащие эту подстроку.'}
                  </div>
                </div>
              )}

              {productSource === 'selected' && (
                <div className="autoseo-hint">
                  {selectedOfferIds.size > 0
                    ? `Будет использовано отмеченных артикулов: ${selectedOfferIds.size}. Отмечайте чекбоксами в таблице ниже.`
                    : 'Ничего не отмечено — поставьте галочки в таблице артикулов ниже.'}
                </div>
              )}
            </div>

            <div className="autoseo-gen-section">
              <div className="autoseo-gen-section-title">
                Выбрать словарь (можно несколько — семантика объединяется)
              </div>
              {dictionaries.length === 0 ? (
                <span className="autoseo-hint">Словарей нет — создайте их во вкладке «Семантика».</span>
              ) : (
                <div className="autoseo-gen-checklist">
                  {dictionaries.map(d => (
                    <label key={d.id} className={`autoseo-gen-option ${genDictionaryIds.includes(d.id) ? 'selected' : ''}`}>
                      <input type="checkbox" checked={genDictionaryIds.includes(d.id)}
                        onChange={() => setGenDictionaryIds(s => s.includes(d.id) ? s.filter(x => x !== d.id) : [...s, d.id])} />
                      {d.name}{d.is_own === false ? ` · ${d.owner_account_name}` : ''} <span className="autoseo-hint">({(d.keyword_count || 0).toLocaleString('ru-RU')})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="autoseo-toolbar">
              <button type="button" className="autoseo-btn autoseo-btn--brand" onClick={generateSeo}
                disabled={articlesBusy || genDictionaryIds.length === 0}>
                Создать SEO описание
              </button>
              <button type="button" className="autoseo-btn primary" onClick={transferToSeo}
                disabled={selectedOfferIds.size === 0}>
                Перенести в SEO (выбранные в таблице)
              </button>
            </div>
          </div>

          {/* Таблица + правка вручную/шаблоном */}
          <div className="autoseo-toolbar">
            <button type="button" className="autoseo-btn" onClick={() => setShowBulkTemplate(v => !v)}>
              Обновить таблицей
            </button>
            <button type="button" className="autoseo-btn" onClick={() => exportArticles(articles)}
              disabled={articles.length === 0}>↓ Экспорт таблицы</button>
            <button type="button" className="autoseo-btn autoseo-btn--danger" onClick={clearArticlesTable}
              disabled={articlesBusy || articles.length === 0}>Очистить таблицу</button>
            <div className="autoseo-spacer" />
            <input type="text" placeholder="Поиск по артикулу/названию..." value={articlesSearch}
              onChange={e => setArticlesSearch(e.target.value)}
              className="autoseo-search" />
          </div>

          {showBulkTemplate && (
            <div className="autoseo-upload">
              <input ref={articlesFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleArticlesFile} />
              <div className="autoseo-hint" style={{ marginBottom: 8 }}>
                Файл: столбцы Артикул, Название, Свойства (как в «Экспорт таблицы»). Или вставьте те же 3 столбца через Tab:
              </div>
              <textarea
                rows={6}
                placeholder={'Артикул\tНазвание\tСвойства\nSKU-001\tНовое название\tсвойство1; свойство2'}
                value={bulkTemplateText}
                onChange={e => setBulkTemplateText(e.target.value)}
              />
              <div className="autoseo-hint">
                Пустая ячейка — поле не меняется.
              </div>
              <div style={{ marginTop: 8 }}>
                <button type="button" className="autoseo-btn autoseo-btn--primary" onClick={handleBulkTemplate} disabled={articlesBusy}>
                  Применить вставленный текст
                </button>
              </div>
            </div>
          )}

          {articlesMessage && <div className="autoseo-message">{articlesMessage}</div>}

          {isArticlesScoped && (
            <div className="autoseo-hint" style={{ padding: '0 16px 8px' }}>
              Таблица отфильтрована по выбору товаров на Шаге 4: {scopedArticles.length.toLocaleString('ru-RU')} из {articles.length.toLocaleString('ru-RU')}.
            </div>
          )}

          {articlesLoading ? <div className="autoseo-hint" style={{ padding: 16 }}>Загрузка…</div> : (
            <>
              <div className="autoseo-scroll">
                <table className="autoseo-table">
                  <thead>
                    <tr>
                      <th className="autoseo-th autoseo-th--check">
                        <input type="checkbox" checked={allVisibleSelected} onChange={() => toggleSelectAllVisible(visibleArticles)} />
                      </th>
                      <th className="autoseo-th autoseo-th--num">№</th>
                      <th className="autoseo-th autoseo-th--offer">Артикул</th>
                      <th className="autoseo-th autoseo-th--name">Название</th>
                      <th className="autoseo-th autoseo-th--props">Свойства</th>
                      <th className="autoseo-th autoseo-th--seo">SEO-описание</th>
                      <th className="autoseo-th autoseo-th--dict">Словарь</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleArticles.map((a, idx) => {
                      const descLen = a.generated_description?.length || 0;
                      const isOver = descLen > 6000;
                      return (
                        <tr className="autoseo-tr" key={a.id}>
                          <td className="autoseo-td autoseo-td--check">
                            <input type="checkbox" checked={selectedOfferIds.has(a.offer_id)} onChange={() => toggleSelectOffer(a.offer_id)} />
                          </td>
                          <td className="autoseo-td autoseo-td--num">{articlesPage * PAGE_SIZE + idx + 1}</td>
                          <td className="autoseo-td autoseo-td--offer">{a.offer_id}</td>
                          <td className="autoseo-td">
                            <textarea rows={2} className="autoseo-cell-input" value={a.name || ''}
                              onChange={e => updateArticle(a.id, { name: e.target.value })} />
                          </td>
                          <td className="autoseo-td">
                            <textarea rows={3} className="autoseo-cell-input autoseo-cell-input--mono" value={a.properties || ''}
                              onChange={e => updateArticle(a.id, { properties: e.target.value })} />
                          </td>
                          <td className="autoseo-td">
                            <textarea rows={4}
                              className="autoseo-cell-input autoseo-cell-input--mono autoseo-cell-input--seo"
                              value={a.generated_description || ''}
                              onChange={e => updateArticle(a.id, { generated_description: e.target.value })} />
                            {descLen > 0 && (
                              <span className={`autoseo-len-badge ${isOver ? 'autoseo-len-badge--over' : ''}`}>
                                {descLen.toLocaleString('ru-RU')} симв.
                              </span>
                            )}
                          </td>
                          <td className="autoseo-td autoseo-td--dict">
                            <div className="autoseo-dict-cell">
                              <div className="autoseo-dict-chips">
                                {effectiveArticleDictIds(a.offer_id).map(id => {
                                  const dict = dictionaries.find(d => d.id === id);
                                  if (!dict) return null;
                                  return (
                                    <span key={id} className="autoseo-dict-chip">
                                      {dict.name}
                                      <button type="button" className="autoseo-dict-chip__remove"
                                        title="Не использовать этот словарь для данного артикула"
                                        onClick={() => toggleArticleDictionary(a.offer_id, id)}>×</button>
                                    </span>
                                  );
                                })}
                                {effectiveArticleDictIds(a.offer_id).length === 0 && (
                                  <span className="autoseo-hint">нет словарей</span>
                                )}
                              </div>
                              <div className="autoseo-dict-dropdown-wrap">
                                <button type="button" className="autoseo-dict-dropdown-toggle"
                                  onClick={e => { e.stopPropagation(); setDictDropdownOfferId(prev => prev === a.offer_id ? null : a.offer_id); }}>
                                  + словарь ▾
                                </button>
                                {dictDropdownOfferId === a.offer_id && (
                                  <div className="autoseo-dict-dropdown" onClick={e => e.stopPropagation()}>
                                    {dictionaries.length === 0 && (
                                      <span className="autoseo-hint">Словарей нет</span>
                                    )}
                                    {dictionaries.map(d => (
                                      <label key={d.id} className="autoseo-dict-dropdown__option">
                                        <input type="checkbox"
                                          checked={effectiveArticleDictIds(a.offer_id).includes(d.id)}
                                          onChange={() => toggleArticleDictionary(a.offer_id, d.id)} />
                                        {d.name}
                                      </label>
                                    ))}
                                    {articleDictOverrides[a.offer_id] && (
                                      <button type="button" className="autoseo-dict-dropdown__reset"
                                        onClick={() => resetArticleDictionaries(a.offer_id)}>
                                        ↺ Вернуть как на Шаге 4
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {visibleArticles.length === 0 && (
                      <tr><td className="autoseo-td autoseo-td--empty" colSpan={7}>
                        Пусто. Выполните Шаг 1 — «Загрузить из Ассортимент — Артикулы».
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {articlesPageCount > 1 && (
                <div className="autoseo-pagination">
                  <button className="autoseo-btn" disabled={articlesPage === 0}
                    onClick={() => setArticlesPage(p => Math.max(0, p - 1))}>←</button>
                  <span className="autoseo-pagination__info">{articlesPage + 1} / {articlesPageCount}</span>
                  <button className="autoseo-btn" disabled={articlesPage >= articlesPageCount - 1}
                    onClick={() => setArticlesPage(p => Math.min(articlesPageCount - 1, p + 1))}>→</button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
